import Navbar from "@/Components/Navbar"
import { useNavigate, useParams } from "react-router-dom"
import backgroundImage from "@/images/backgrounds/lobby-background.webp"
import { useEffect, useState } from "react"
import { socket } from "@/utils/utils"
import NewGameSettings from "./NewGameSettings"
import Rank from "@/Components/Rank"
import rankImage from "@/images/rank.webp"
import GlobalChat from "@/Components/GlobalChat"
import { featureFlags } from "@mono/constants"
import { useAppSelector } from "@/store/hooks"
import GameNotAvailable from "@/Pages/Game/GameNotAvailable"
import LeaveGameConfirmModal from "@/Modals/LeaveGameConfirmModal"

interface NewGameProps {}

const NewGame: React.FC<NewGameProps> = () => {
  const params = useParams()
  const id = params.id
  const nav = useNavigate()
  const myGame = useAppSelector((state) => state.global.myGame)
  const leaveConfirmVisible = useAppSelector(
    (state) => state.global.modalData.leaveGameConfirm?.visible
  )

  // The lobby's details arrive asynchronously (server response to "joinRoom"),
  // so a missing game right after mount just means it is still loading. Wait
  // out a short grace period before deciding the game is actually gone, to
  // avoid flashing the not-available page — same as the game page does.
  const isGameLoaded = !!myGame && myGame.id === id
  const [gaveUpWaiting, setGaveUpWaiting] = useState(false)

  // Joining the room is what makes the server send this lobby's details, so it
  // has to happen above the early returns below — otherwise a game that is
  // merely still loading would never get the chance to load.
  useEffect(() => {
    socket.emit("joinRoom", { roomid: id as string }, (res) => {
      if (!res) {
        nav("/lobby")
      }
    })
    socket.emit("newgame.requestGameList")
  }, [])

  useEffect(() => {
    if (isGameLoaded) {
      setGaveUpWaiting(false)
      return
    }
    const timeout = setTimeout(() => setGaveUpWaiting(true), 5000)
    return () => clearTimeout(timeout)
  }, [isGameLoaded])

  if (!isGameLoaded) {
    if (gaveUpWaiting)
      return <GameNotAvailable backgroundUrl={backgroundImage} />

    // Still within the grace period — show a lightweight loader while the
    // lobby details are fetched.
    return (
      <div
        data-testid="newgame.loading"
        className="w-screen h-screen bg-cover bg-center flex items-center justify-center overflow-hidden"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      >
        <div className="h-14 w-14 rounded-full border-4 border-white/30 border-t-white animate-spin" />
      </div>
    )
  }

  return (
    <div
      data-testid="lobby"
      className="w-screen h-screen bg-cover md:overflow-auto"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      <div className="fixed top-0 left-0 z-[100] md:static w-full">
        <Navbar></Navbar>
      </div>
      {/* <div className="grid grid-cols-[1fr_3fr] gap-x-8 pt-20"> */}
      {/* <div className="grid grid-rows-[min-content_auto] gap-8">
          <Rank image={rankImage} name="Grandmaster" number="1"></Rank>
        </div> */}
      <div className="p-20 sm:p-0 sm:h-full ">
        <NewGameSettings myGameId={id as string}></NewGameSettings>
      </div>
      {/* </div> */}

      {featureFlags.showChat && <GlobalChat />}

      {/* Opened by the Android back button (NativeBackHandler): backing out
          of a room hands the seat back, so it asks first. */}
      {leaveConfirmVisible && (
        <LeaveGameConfirmModal variant="waiting-room" />
      )}
    </div>
  )
}

export default NewGame
